import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { useInView, useMotionValue } from "framer-motion";

const aboutDetails = [
  {
    title: "Our Mission",
    color: "from-blue-500 to-cyan-500",
    icon: (
      <svg
        className="w-10 h-10 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M13 10V3L4 14h7v7l9-11h-7z"
        />
      </svg>
    ),
    content: (
      <>
        <p className="text-lg text-gray-600 mb-2">
          To empower businesses with innovative digital solutions that drive
          growth and create meaningful connections with their audience.
        </p>
        <ul className="list-disc pl-6 text-gray-500 text-base space-y-1">
          <li>Delivering measurable results for our clients</li>
          <li>Fostering creativity and innovation</li>
          <li>Building long-term partnerships</li>
        </ul>
      </>
    ),
  },
  {
    title: "Our Vision",
    color: "from-purple-500 to-pink-500",
    icon: (
      <svg
        className="w-10 h-10 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
        />
      </svg>
    ),
    content: (
      <>
        <p className="text-lg text-gray-600 mb-2">
          To be the leading digital marketing agency that transforms how
          businesses connect and grow in the digital landscape.
        </p>
        <ul className="list-disc pl-6 text-gray-500 text-base space-y-1">
          <li>Setting new standards in digital marketing</li>
          <li>Inspiring innovation and excellence</li>
          <li>Empowering businesses worldwide</li>
        </ul>
      </>
    ),
  },
  {
    title: "Our Values",
    color: "from-green-500 to-emerald-500",
    icon: (
      <svg
        className="w-10 h-10 text-white"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 8c-1.657 0-3 1.343-3 3 0 1.657 1.343 3 3 3s3-1.343 3-3c0-1.657-1.343-3-3-3z"
        />
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 2v2m0 16v2m10-10h-2M4 12H2m15.364-7.364l-1.414 1.414M6.05 17.95l-1.414 1.414m12.728 0l-1.414-1.414M6.05 6.05L4.636 4.636"
        />
      </svg>
    ),
    content: (
      <>
        <p className="text-lg text-gray-600 mb-2">
          We are guided by integrity, creativity, and a commitment to excellence
          in everything we do.
        </p>
        <ul className="list-disc pl-6 text-gray-500 text-base space-y-1">
          <li>Integrity and transparency</li>
          <li>Collaboration and respect</li>
          <li>Continuous learning and growth</li>
        </ul>
      </>
    ),
  },
];

// AnimatedNumber helper
const AnimatedNumber = ({ value, duration = 1.2 }) => {
  const ref = useRef();
  const inView = useInView(ref, { once: true, margin: "-100px" });
  const motionValue = useMotionValue(0);
  const [display, setDisplay] = useState(0);

  useEffect(() => {
    if (inView) {
      const start = performance.now();
      const animate = (now) => {
        const elapsed = (now - start) / 1000;
        const progress = Math.min(elapsed / duration, 1);
        const current = Math.round(progress * value);
        setDisplay(current);
        if (progress < 1) requestAnimationFrame(animate);
        else setDisplay(value);
      };
      requestAnimationFrame(animate);
    }
  }, [inView, value, duration]);

  return <span ref={ref}>{display}</span>;
};

const About = () => {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <section id="about" className="py-20 bg-white relative overflow-hidden">
      <div className="container mx-auto px-4 relative z-10">
        {/* Header */}
        <motion.div className="text-center mb-16">
          <motion.h2 className="text-5xl font-bold text-gray-800 mb-6 font-poppins">
            About Us
          </motion.h2>
          <motion.p className="text-xl text-gray-600 max-w-4xl mx-auto font-poppins leading-relaxed">
            We are a team of passionate digital marketers, designers, and
            developers dedicated to helping businesses achieve their online
            goals through innovative solutions and strategic thinking.
          </motion.p>
        </motion.div>

        {/* About Details */}
        <motion.div className="grid md:grid-cols-3 gap-12">
          {aboutDetails.map((item, idx) => (
            <motion.div
              key={item.title}
              className="text-center group cursor-pointer relative"
              onClick={() => setOpenIndex(idx)}
            >
              <div
                className={`w-20 h-20 bg-gradient-to-br ${item.color} rounded-full flex items-center justify-center mx-auto mb-6`}
              >
                {item.icon}
              </div>
              <h3 className="text-2xl font-semibold text-gray-800 mb-4 font-poppins">
                {item.title}
              </h3>
              <span className="text-gray-600 font-poppins leading-relaxed block">
                {item.content.props.children[0].props.children}
              </span>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats */}
        <div className="mt-20 text-center">
          <h3 className="text-3xl font-semibold text-gray-800 mb-8 font-poppins">
            Why Choose Us?
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { number: 5, label: "Years Experience", suffix: "+" },
              { number: 100, label: "Happy Clients", suffix: "+" },
              { number: 200, label: "Projects Completed", suffix: "+" },
              { number: 24, label: "Support Available", suffix: "/7" },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div className="text-4xl font-bold text-blue-600 mb-2">
                  <AnimatedNumber value={stat.number} />
                  {stat.suffix}
                </div>
                <p className="text-gray-600 font-poppins">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Modal */}
        <AnimatePresence>
          {openIndex !== null && (
            <motion.div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
              <motion.div className="relative rounded-3xl shadow-2xl flex flex-col items-center justify-center w-full max-w-lg mx-4 bg-white border border-gray-200 p-10">
                <button
                  className="absolute top-4 right-4 p-2 rounded-full bg-gray-200 hover:bg-gray-300 text-gray-800"
                  onClick={() => setOpenIndex(null)}
                  aria-label="Close"
                >
                  <XMarkIcon className="w-6 h-6" />
                </button>
                <div
                  className={`w-20 h-20 bg-gradient-to-br ${aboutDetails[openIndex].color} rounded-2xl flex items-center justify-center mb-6`}
                >
                  {aboutDetails[openIndex].icon}
                </div>
                <h3 className="text-3xl font-extrabold text-gray-800 mb-4 text-center">
                  {aboutDetails[openIndex].title}
                </h3>
                <div className="mb-4 w-full max-w-xs mx-auto text-gray-600 text-lg text-center">
                  {aboutDetails[openIndex].content}
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default About;
